import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import HashLoader from "react-spinners/ClipLoader";

import { Divider, Modal, notification } from "antd";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import PickupEmail from "./PickupEmail";
import fetchSiteList from "../../../api/sites/fetchSiteList";

const defaultTemplate = "<p>Your pickup code is {{pickup_code}}.</p>";

const CommunicationList = () => {
  const url = process.env.REACT_APP_APP_BACK_URL;
  const isAuthenticated = useSelector((state) => state.user);
  const isSuperAdmin =
    String(isAuthenticated?.role || "").toLowerCase() === "superadmin";
  const isSiteAdmin =
    String(isAuthenticated?.role || "").toLowerCase() === "admin";

  const [showModal, setShowModal] = useState(false);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [templateId, setTemplateId] = useState(null);
  const [templateReady, setTemplateReady] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);

  useEffect(() => {
    if (isSiteAdmin && isAuthenticated?.site_id) {
      setSiteId(isAuthenticated.site_id);
    }
  }, [isSiteAdmin, isAuthenticated?.site_id]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      const siteData = await fetchSiteList();
      const list = siteData?.site_data || [];
      setSites(list);
      if (list[0]?._id) {
        setSiteId(list[0]._id);
      } else {
        setTemplateReady(true);
      }
    })();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!siteId || (!isSiteAdmin && !isSuperAdmin)) return;
    let cancelled = false;
    const loadTemplate = async () => {
      setTemplateReady(false);
      try {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("auth-token", localStorage.getItem("token"));
        const response = await fetch(
          `${url}/api/communication/ge/email/template?site_id=${siteId}`,
          { method: "GET", headers }
        );
        const json = await response.json();
        if (cancelled) return;
        if (json?.success && json?.data?.content) {
          setTemplateContent(json.data.content);
          setTemplateId(json.data._id);
        } else {
          setTemplateContent(defaultTemplate);
          setTemplateId(null);
        }
      } catch (error) {
        if (!cancelled) {
          setTemplateContent(defaultTemplate);
          setTemplateId(null);
        }
      } finally {
        if (!cancelled) setTemplateReady(true);
      }
    };
    loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [isSiteAdmin, isSuperAdmin, siteId, url]);

  const saveTemplate = async () => {
    if (!siteId) return;
    try {
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      headers.append("auth-token", localStorage.getItem("token"));
      setTemplateSaving(true);
      const response = await fetch(
        `${url}/api/communication/update/email/template/${templateId || "null"}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            content: templateContent,
            site_id: siteId,
          }),
        }
      );
      const json = await response.json();
      if (json.success) {
        if (json.data?._id) setTemplateId(json.data._id);
        notification.success({
          message: "Success",
          description: "Pickup message updated",
          duration: 3,
        });
      } else {
        notification.error({
          message: "Failed",
          description: json.error || "Could not save the pickup message",
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: "Failed",
        description: "Could not save the pickup message",
        duration: 3,
      });
    } finally {
      setTemplateSaving(false);
    }
  };

  return (
    <div className="general-dashboard mt-3">
      <div className="container-fluid">
        <div className="dashboard-top-row">
          <div className="row">
            <div className="col-lg-6 my-auto">
              <div className="user-wrap">
                <h3>Pickup message</h3>
                <p>
                  {isSuperAdmin
                    ? "Edit the text sent in pickup emails for the selected site."
                    : "This is the text sent in your site pickup emails. You can change it for your site."}
                </p>
              </div>
            </div>
            <div className="col-lg-6 text-end">
              <div className="filter-right-sec">
                <div className="d-flex flex-row gap-3">
                  {isSuperAdmin && (
                    <button
                      onClick={() => {
                        setShowModal(true);
                      }}
                      className="btn btn-prim w-auto"
                    >
                      Pickup Email
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {(isSiteAdmin || isSuperAdmin) && (
          <div className="form-box card-box">
            {isSuperAdmin && (
              <div className="mb-3" style={{ maxWidth: 360 }}>
                <label className="form-label" htmlFor="pickup-message-site">
                  Site
                </label>
                <select
                  id="pickup-message-site"
                  className="form-control"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                >
                  <option value="">Choose Site</option>
                  {sites.map((site) => (
                    <option key={site._id} value={site._id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <label className="form-label">Email text</label>
            <p>Use {"{{pickup_code}}"} where the pickup code should appear.</p>
            <div
              style={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
                marginBottom: "20px",
              }}
            >
              {templateReady ? (
                <CKEditor
                  key={siteId || "site"}
                  editor={ClassicEditor}
                  data={templateContent}
                  onReady={(editor) => {
                    editor.ui.view.editable.element.style.minHeight = "220px";
                  }}
                  onChange={(event, editor) => {
                    setTemplateContent(editor.getData());
                  }}
                />
              ) : (
                <HashLoader color="#0b0d41" loading size={40} />
              )}
            </div>
            <div className="text-end">
              <button
                type="button"
                className="btn btn-prim w-auto"
                disabled={templateSaving || !templateReady || !siteId}
                onClick={saveTemplate}
              >
                {templateSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
      <Modal
        title="Pickup Email"
        open={showModal}
        onOk={() => {}}
        onCancel={() => {
          setShowModal(false);
        }}
        footer={null}
      >
        <Divider />
        <PickupEmail />
      </Modal>
    </div>
  );
};
export default CommunicationList;
